import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { MAIN_SITE_URL, CATEGORIES } from '@/mocks/categories';

const ANCHOR_ITEMS = [
  { key: 'nav.spreadsheet', href: '#spreadsheet' },
  { key: 'nav.guides', href: '#guides' },
  { key: 'nav.reviews', href: '#features' },
  { key: 'nav.shipping', href: '#categories' },
  { key: 'nav.safety', href: '#safety' },
  { key: 'nav.faq', href: '#faq' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-40 border-b-2 border-background-200 bg-white">
      <nav className="flex items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link to="/" className="flex items-center gap-2 cursor-pointer">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-file-list-3-line text-lg" />
          </span>
          <span className="font-heading text-base font-bold text-foreground-950 whitespace-nowrap">
            USFans <span className="text-primary-600">Spreadsheet</span>
          </span>
        </Link>

        <div className="hidden items-center gap-5 lg:flex">
          <Link
            to="/"
            className="text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-600 whitespace-nowrap"
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/article/shopping-guide"
            className="text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-600 whitespace-nowrap"
          >
            {t('nav.shopping_guide')}
          </Link>
          {ANCHOR_ITEMS.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="text-sm font-semibold text-foreground-800 transition-colors hover:text-primary-600 whitespace-nowrap"
            >
              {t(item.key)}
            </a>
          ))}
        </div>

        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            {t('nav.visit')}
            <i className="ri-arrow-right-up-line" />
          </a>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground-800 hover:bg-background-100 lg:hidden cursor-pointer"
          aria-label="Menu"
        >
          <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </nav>

      {mobileOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-200 bg-white px-4 py-4 lg:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/article/shopping-guide"
              onClick={() => setMobileOpen(false)}
              className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100 cursor-pointer"
            >
              {t('nav.shopping_guide')}
            </Link>
            {ANCHOR_ITEMS.map((item) => (
              <a
                key={item.key}
                href={item.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-3 py-2.5 text-sm font-semibold text-foreground-800 hover:bg-background-100"
              >
                {t(item.key)}
              </a>
            ))}
          </div>

          <p className="mt-4 px-3 text-xs font-semibold uppercase tracking-wide text-foreground-500">
            {t('category.title')}
          </p>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            {CATEGORIES.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg border border-background-200/70 bg-background-50 px-3 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center text-primary-600">
                  <i className={cat.icon} />
                </span>
                <span className="truncate">{t(`cat.${cat.key}`)}</span>
              </a>
            ))}
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <LanguageSwitcher />
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 whitespace-nowrap"
            >
              {t('nav.visit')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}